/**
 * request 网络请求工具
 * 更详细的 api 文档: https://github.com/umijs/umi-request
 */
import { extend } from 'umi-request';
import type { ResponseError } from 'umi-request';
import { Toast } from 'antd-mobile';
import {
    getUrlParams,
    isEcoflowApp,
    getAppCurrentCountryCode,
    getAppCurrentVersion,
    getAppCurrentPlatform,
    getAppAcceptLanguage,
    getAppToken,
    getRequestHeadersLangString,
    getAppCurrentLang,
    LangType,
} from '@/utils/tools';
import { X_APPID, PRO_LOGIN_TYPE } from '@/constants/pro';

interface Code {
    [propName: number]: string;
}

interface AcceptLanguage {
    [key: string]: string;
}

const controller = new AbortController();
const { signal } = controller;

// response code message
const codeMessage: Code = {
    200: '服务器成功返回请求的数据。',
    201: '新建或修改数据成功。',
    202: '一个请求已经进入后台排队（异步任务）。',
    204: '删除数据成功。',
    400: '发出的请求有错误，服务器没有进行新建或修改数据的操作。',
    401: '用户没有权限（令牌、用户名、密码错误）。',
    403: '用户得到授权，但是访问是被禁止的。',
    404: '发出的请求针对的是不存在的记录，服务器没有进行操作。',
    406: '请求的格式不可得。',
    410: '请求的资源被永久删除，且不会再得到的。',
    422: '当创建一个对象时，发生一个验证错误。',
    500: '服务器发生错误，请检查服务器。',
    502: '网关错误。',
    503: '服务不可用，服务器暂时过载或维护。',
    504: '网关超时。',
};

// query lang match accept-language
export const matchAcceptLang = (lang = 'us') => {
    const acceptLangs: AcceptLanguage = {
        us: 'en-US,en',
        jp: 'ja-JP,ja',
        cn: 'zh-CN,zh',
        fr: 'fr-FR,fr',
        de: 'de-DE,de',
        it: 'it-IT,it',
        ko: 'ko-KR,ko',
        ru: 'ru-RU,ru',
        es: 'es-ES,es',
        nl: 'nl-NL,nl',
        uk: 'uk-UA,uk', // 乌克兰语
        tw: 'zh-TW,zh',
    };

    const acceptedLanguage: string = acceptLangs[lang] ? ''.concat(acceptLangs[lang], ';q=0.9') : 'en-US,en;q=0.9';
    return acceptedLanguage;
};

// query lang match request lang
export const requestLangMap = (lang = 'us') => {
    const requestLangs: AcceptLanguage = {
        us: 'en_US',
        jp: 'ja_JP',
        cn: 'zh_CN',
        fr: 'fr_FR',
        de: 'de_DE',
        it: 'it_IT',
        ko: 'ko_KR',
        ru: 'ru_RU',
        es: 'es_ES',
        nl: 'nl_NL',
        uk: 'uk_UA', //乌克兰语
        tw: 'zh_TW',
        br: 'pt_BR',
    };

    return requestLangs[lang] || 'en_US';
};

// error handle procedure
const errorHandler = (error: ResponseError): any => {
    const { response, data } = error;
    if (error.response && error.response.status) {
        const errorText = codeMessage[response.status] || response.statusText;
        const { status, url } = response;
        if (isEcoflowApp()) {
            console.error({
                message: `请求错误 ${status}: ${url}`,
                description: errorText + JSON.stringify(response, null, 2),
            });
        } else {
            Toast.show({
                icon: 'fail',
                content: errorText,
            });
        }
    } else if (error.name && error.name === 'RequestError') {
        // 超时处理
        if (error.type === 'Timeout') {
            controller.abort();
        }
    }
    return data;
};

// default request configuration
const request = extend({
    errorHandler,
    timeout: 30000, // 请求超时时间
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Accept-Language': 'en-US,en;q=0.9',
    },
    signal,
});

request.interceptors.request.use((url: string, options: any) => {
    const params = getUrlParams(location.href);
    (options.headers as any)['Authorization'] = getAppToken();
    (options.headers as any)['Accept-Language'] = getAppAcceptLanguage();
    // 后端需要的headers参数
    (options.headers as any)['countryCode'] = getAppCurrentCountryCode() || '';
    (options.headers as any)['version'] = getAppCurrentVersion() || '';
    (options.headers as any)['platform'] = getAppCurrentPlatform() || '';
    (options.headers as any)['lang'] = getRequestHeadersLangString(getAppCurrentLang() as LangType);
    if (params.loginType && params.loginType === PRO_LOGIN_TYPE) {
        (options.headers as any)['X-Appid'] = X_APPID;
    }
    return {
        url,
        options,
    };
});

request.interceptors.response.use((response: any) => {
    return response;
});

export default request;
